import { useEffect, useMemo, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthGate } from "./components/auth-gate";
import { AppShell, type PageKey } from "./components/app-shell";
import { AccountsPage } from "./features/accounts/accounts-page";
import { NotesPage } from "./features/notes/notes-page";
import { TasksPage } from "./features/tasks/tasks-page";
import { ProfilesPage } from "./features/profiles/profiles-page";
import { SettingsPage } from "./features/settings/settings-page";

function readPage(): PageKey {
  const hashValue = window.location.hash.replace("#", "");
  if (hashValue === "profiles" || hashValue === "tasks" || hashValue === "notes" || hashValue === "settings") return hashValue;
  const value = window.location.pathname.replace(/^\/+/, "").split("/")[0];
  if (value === "profiles" || value === "tasks" || value === "notes" || value === "settings") return value;
  return "accounts";
}

function pathForPage(page: PageKey) {
  return page === "accounts" ? "/accounts" : `/${page}`;
}

export function App() {
  const client = useMemo(() => new QueryClient(), []);
  const [page, setPage] = useState<PageKey>(() => readPage());

  useEffect(() => {
    function syncPage() {
      setPage(readPage());
    }
    window.addEventListener("popstate", syncPage);
    window.addEventListener("hashchange", syncPage);
    return () => {
      window.removeEventListener("popstate", syncPage);
      window.removeEventListener("hashchange", syncPage);
    };
  }, []);

  function changePage(next: PageKey) {
    window.history.pushState({}, "", pathForPage(next));
    setPage(next);
  }
  return (
    <QueryClientProvider client={client}>
      <AuthGate>
        <AppShell page={page} onPageChange={changePage}>
          {page === "accounts" ? <AccountsPage /> : null}
          {page === "profiles" ? <ProfilesPage /> : null}
          {page === "tasks" ? <TasksPage /> : null}
          {page === "notes" ? <NotesPage /> : null}
          {page === "settings" ? <SettingsPage /> : null}
        </AppShell>
      </AuthGate>
      <Toaster position="top-right" richColors />
    </QueryClientProvider>
  );
}
